import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * useScrollReveal — automatically observes every element with the
 * `.reveal` class (see ScrollFX.css) and adds `.reveal-visible` once
 * it scrolls into view. Re-scans whenever the route changes so newly
 * mounted pages pick up their own reveal elements.
 *
 * Usage: just call useScrollReveal() once, high up in the app
 * (e.g. inside ClientLayout). Then any section/card/element can opt
 * into the animation simply by adding className="reveal" (optionally
 * with reveal-left / reveal-right / reveal-down / reveal-zoom and
 * reveal-delay-1..6, all defined in ScrollFX.css).
 */
export default function useScrollReveal() {
  const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const observeAllPending = () => {
      document
        .querySelectorAll(".reveal:not(.reveal-visible)")
        .forEach((el) => observer.observe(el));
    };

    // Initial scan once the route's DOM has painted
    const initialTimer = setTimeout(observeAllPending, 60);

    // Also watch for elements added later — e.g. product/blog/gallery
    // cards that only appear after an async API call resolves. Without
    // this, anything rendered after the initial scan would stay stuck
    // at opacity: 0 forever (only "fixed" by a full page refresh).
    const mutationObserver = new MutationObserver(() => {
      observeAllPending();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      clearTimeout(initialTimer);
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [location.pathname]);
}
